import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ImageOff, Maximize2, Minimize2, X } from "lucide-react";
import { A11y, Keyboard, Navigation, Zoom } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import { api } from "../api";
import type { Client, DisplaySettings } from "../types";
import { overlayThemeVariables } from "../overlayTheme";

const fallbackSettings = {
  galleryFallbackLocation: "Algarve, Portugal",
  galleryEmptyTitle: "Images coming soon",
  imageUnavailableText: "Image unavailable"
};

function notifyParent(type: "SHOWROOM_GALLERY_CLOSE" | "SHOWROOM_GALLERY_ACTIVITY") {
  window.parent.postMessage({ type }, window.location.origin);
}

function ProjectImage({ src, alt, unavailableText }: { src: string; alt: string; unavailableText: string }) {
  const [failed, setFailed] = useState(false);
  const [orientation, setOrientation] = useState<"landscape" | "portrait" | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    setFailed(false);
    setOrientation(null);
    return () => {
      if (!imageRef.current) return;
      imageRef.current.removeAttribute("src");
      imageRef.current.src = "";
    };
  }, [src]);

  return failed
    ? <div className="image-fallback"><ImageOff /><span>{unavailableText}</span></div>
    : <img ref={imageRef}
      className={"gallery-image" + (orientation ? " is-" + orientation : "")}
      src={src}
      alt={alt}
      loading="eager"
      decoding="async"
      onLoad={(event) => {
        const image = event.currentTarget;
        setOrientation(image.naturalHeight > image.naturalWidth ? "portrait" : "landscape");
      }}
      onError={() => setFailed(true)} />;
}

function GalleryMediaSlot({
  active, mediaReady, mediaType, src, alt, unavailableText
}: { active: boolean; mediaReady: boolean; mediaType: "image" | "video"; src: string; alt: string; unavailableText: string }) {
  if (!active || !mediaReady) return <div className="gallery-image-placeholder" aria-hidden="true" />;
  return mediaType === "video"
    ? <div className="gallery-video-frame"><video className="gallery-video" src={src} controls playsInline preload="metadata" /></div>
    : <ProjectImage src={src} alt={alt} unavailableText={unavailableText} />;
}

export default function GalleryFrame() {
  const { clientId } = useParams();
  const [currentClientId, setCurrentClientId] = useState(clientId || "");
  const [client, setClient] = useState<Client | null>(null);
  const [settings, setSettings] = useState<Partial<DisplaySettings>>(fallbackSettings);
  const [activeSlide, setActiveSlide] = useState(0);
  const [mediaReady, setMediaReady] = useState(false);
  const [focusMode, setFocusMode] = useState(false);
  const swiper = useRef<SwiperType | null>(null);
  const mediaTimer = useRef<number | null>(null);
  const loadedClientId = useRef<number | null>(null);

  const revealMediaAfterShell = () => {
    if (mediaTimer.current) window.clearTimeout(mediaTimer.current);
    setMediaReady(false);
    window.requestAnimationFrame(() => {
      mediaTimer.current = window.setTimeout(() => setMediaReady(true), 80);
    });
  };

  useEffect(() => {
    const receiveMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === "SHOWROOM_GALLERY_CLEAR") {
        setMediaReady(false);
        setClient(null);
        setCurrentClientId("");
        setActiveSlide(0);
        setFocusMode(false);
        loadedClientId.current = null;
        swiper.current?.slideTo(0, 0);
        return;
      }
      if (event.data?.type === "SHOWROOM_GALLERY_OPEN" && event.data.clientId) {
        const nextClientId = Number(event.data.clientId);
        if (loadedClientId.current === nextClientId) {
          if (event.data.settings) setSettings(event.data.settings);
          return;
        }
        setActiveSlide(0);
        setFocusMode(false);
        swiper.current?.slideTo(0, 0);
        if (event.data.client) {
          setCurrentClientId("");
          loadedClientId.current = nextClientId;
          setClient(event.data.client);
          if (event.data.settings) setSettings(event.data.settings);
          revealMediaAfterShell();
          return;
        }
        setCurrentClientId(String(event.data.clientId));
      }
    };
    window.addEventListener("message", receiveMessage);
    window.parent.postMessage({ type: "SHOWROOM_GALLERY_READY" }, window.location.origin);
    return () => window.removeEventListener("message", receiveMessage);
  }, []);

  useEffect(() => {
    if (!currentClientId) return;
    let cancelled = false;
    const load = async () => {
      try {
        setMediaReady(false);
        setActiveSlide(0);
        swiper.current?.slideTo(0, 0);
        const [nextClient, nextSettings] = await Promise.all([
          api<Client>("/api/clients/" + currentClientId),
          api<DisplaySettings>("/api/settings")
        ]);
        if (cancelled) return;
        loadedClientId.current = nextClient.id;
        setClient(nextClient);
        setSettings(nextSettings);
        window.requestAnimationFrame(() => {
          mediaTimer.current = window.setTimeout(() => {
            if (!cancelled) setMediaReady(true);
          }, 80);
        });
      } catch {
        notifyParent("SHOWROOM_GALLERY_CLOSE");
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [currentClientId]);

  useEffect(() => () => {
    if (mediaTimer.current) window.clearTimeout(mediaTimer.current);
  }, []);

  const activity = () => notifyParent("SHOWROOM_GALLERY_ACTIVITY");
  const close = () => notifyParent("SHOWROOM_GALLERY_CLOSE");
  const selectThumbnail = (index: number) => {
    setActiveSlide(index);
    swiper.current?.slideTo(index);
    activity();
  };
  const previousImage = () => {
    setActiveSlide((index) => Math.max(0, index - 1));
    swiper.current?.slidePrev();
    activity();
  };
  const nextImage = () => {
    setActiveSlide((index) => Math.min(Math.max((client?.images.length || 1) - 1, 0), index + 1));
    swiper.current?.slideNext();
    activity();
  };
  const toggleFocusMode = () => {
    setFocusMode((enabled) => !enabled);
    activity();
  };

  const images = client?.images || [];
  return <main className="gallery-frame-page"
    style={overlayThemeVariables(settings.overlayActionBackgroundColor || "#202724")}
    onPointerDown={activity} onWheel={activity}>
    <section className={"gallery-overlay is-open" + (focusMode ? " is-focus" : "")}>
      {!client && <div className="gallery-loading" aria-label="Loading gallery"><span /></div>}
      {client && <>
        <aside className="gallery-top">
          <div className="gallery-sidebar-head">
            <div className="slide-count"><strong>{String(activeSlide + 1).padStart(2, "0")}</strong><span>/</span>
              <span>{String(Math.max(images.length, 1)).padStart(2, "0")}</span></div>
          </div>
          <div className="gallery-title">
            <span className="eyebrow">{client.address || settings.galleryFallbackLocation}</span>
            <h1>{client.name}</h1>
            {client.description && <p>{client.description}</p>}
          </div>
          {images.length > 0 && <div className="gallery-thumbnails" aria-label="Project images">
            {images.map((image, index) => <button key={image.id}
              className={index === activeSlide ? "active" : ""}
              onClick={() => selectThumbnail(index)}
              aria-label={"View image " + (index + 1)}
              aria-pressed={index === activeSlide}>
              {mediaReady ? <img src={image.thumbnailPath} alt="" loading="lazy" decoding="async" /> : <span />}
              <span>{String(index + 1).padStart(2, "0")}</span>
            </button>)}
          </div>}
          {images.length > 0 && <div className="gallery-nav gallery-nav-sidebar">
            {images.length > 1 && <button onClick={previousImage} aria-label="Previous image"><ArrowLeft /></button>}
            {images.length > 1 && <button onClick={nextImage} aria-label="Next image"><ArrowRight /></button>}
            <button onClick={toggleFocusMode} aria-label={focusMode ? "Show thumbnails" : "Hide thumbnails"}>
              {focusMode ? <Minimize2 /> : <Maximize2 />}
            </button>
            <button onClick={close} aria-label="Close project"><X /></button>
          </div>}
        </aside>

        <div className="gallery-stage">
          {images.length ? <Swiper key={client.id}
            modules={[Navigation, Keyboard, A11y, Zoom]}
            speed={0}
            keyboard
            threshold={6}
            longSwipesRatio={0.12}
            longSwipesMs={120}
            touchRatio={1.15}
            resistanceRatio={0.55}
            shortSwipes
            followFinger
            watchSlidesProgress={false}
            lazyPreloadPrevNext={0}
            zoom={{ minRatio: 1, maxRatio: 4, toggle: true }}
            onSwiper={(instance) => { swiper.current = instance; }}
            onSlideChange={(instance) => {
              instance.zoom.out();
              setActiveSlide(instance.activeIndex);
              activity();
            }}
            onTouchMove={activity}
            className="project-swiper">
            {images.map((image, index) => <SwiperSlide key={image.id}>
              <div className="swiper-zoom-container">
                <GalleryMediaSlot active={index === activeSlide}
                  mediaReady={mediaReady}
                  mediaType={image.mediaType || "image"}
                  src={image.filePath}
                  alt={client.name + " project view " + (index + 1)}
                  unavailableText={settings.imageUnavailableText || fallbackSettings.imageUnavailableText} />
              </div>
            </SwiperSlide>)}
          </Swiper> : <div className="empty-gallery"><ImageOff /><h2>{settings.galleryEmptyTitle || fallbackSettings.galleryEmptyTitle}</h2></div>}

          {images.length > 0 && <div className="gallery-nav gallery-nav-focus">
            {images.length > 1 && <button onClick={previousImage} aria-label="Previous image"><ArrowLeft /></button>}
            {images.length > 1 && <button onClick={nextImage} aria-label="Next image"><ArrowRight /></button>}
            <button onClick={toggleFocusMode} aria-label={focusMode ? "Show thumbnails" : "Hide thumbnails"}>
              {focusMode ? <Minimize2 /> : <Maximize2 />}
            </button>
            <button onClick={close} aria-label="Close project"><X /></button>
          </div>}
        </div>
      </>}
    </section>
  </main>;
}
